// ─────────────────────────────────────────────────────────────────────────────
// AmountCard — shows what the payer is paying.
//
// Three rendering cases, driven by the BFF's (amountDisplay, amountKobo,
// currency) triple:
//
//   1. amountDisplay is a string        → fixed NGN amount, server-formatted
//      "Amount to pay: ₦61,800.00"        (Model A, today's main case)
//
//   2. amountDisplay is null AND          → fixed non-NGN amount; we render
//      amountKobo is a number              major-unit manually with the
//      "Amount to pay: 250.00 USD"         currency suffix
//
//   3. amountKobo is null (regardless    → variable-amount intent
//      of amountDisplay)                   (Model B — persistent VA,
//      "Pay any amount"                    future wallet-funding / beneficiary
//                                          credit flows per Phase D)
//
// Why server-formats NGN but frontend-formats other currencies:
//   Per services/api-gateway/src/checkout/checkout.service.ts formatAmount:
//   the BFF deterministically formats NGN (ICU drift would break en-NG).
//   Other currencies fall through to null because Posteering doesn't
//   actively handle them yet. If/when we support USD/GBP inbound, the
//   BFF extends its formatter and this component's fallback becomes
//   dead code — correct trajectory.
//
// The breakdown row (Amount/Fee) is shown only in case 1 — a fixed NGN
// amount with a zero-fee inline. Cases 2 and 3 elide it: non-NGN amounts
// don't yet have fee semantics, and variable amounts can't preview a fee.
// ─────────────────────────────────────────────────────────────────────────────

export interface AmountCardProps {
  amountDisplay: string | null;
  amountKobo: number | null;
  currency: string;
}

export function AmountCard({
  amountDisplay,
  amountKobo,
  currency,
}: AmountCardProps) {
  // Case 3: variable-amount intent. No hero number, no breakdown.
  if (amountKobo === null) {
    return (
      <div className="amount-card">
        <div className="amount-label">Amount to pay</div>
        <div className="amount-hero unknown">Pay any amount</div>
      </div>
    );
  }

  // Case 1: server-formatted NGN string. Preferred path; deterministic.
  if (amountDisplay !== null) {
    return (
      <div className="amount-card">
        <div className="amount-label">Amount to pay</div>
        <div className="amount-hero">{amountDisplay}</div>
        <div className="amount-breakdown">
          <span>
            Amount <strong>{amountDisplay}</strong>
          </span>
          <span className="amount-breakdown-sep" aria-hidden="true" />
          <span>
            Fee <strong>₦0.00</strong>
          </span>
        </div>
      </div>
    );
  }

  // Case 2: non-NGN, server didn't format, render major units manually.
  // This is a defensive path — today the BFF only handles NGN, so we'd
  // only hit this if the schema gains non-NGN without a formatter update.
  const major = (amountKobo / 100).toFixed(2);
  return (
    <div className="amount-card">
      <div className="amount-label">Amount to pay</div>
      <div className="amount-hero">
        {major} {currency}
      </div>
    </div>
  );
}
